import { Link } from "@tanstack/react-router";
import { type FC, type ReactNode } from "react";

import { m } from "@recobra/i18n/messages";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@recobra/ui/breadcrumb";
import { Card, CardContent, CardHeader } from "@recobra/ui/card";
import { ScrollArea } from "@recobra/ui/scroll-area";
import { Separator } from "@recobra/ui/separator";
import { Skeleton } from "@recobra/ui/skeleton";

import { FormPageLayoutGrid } from "./FormPageLayout";
import { useNavHeader } from "./useNavHeader";

const SkeletonCard: FC<{ rows?: number; twoColumn?: boolean }> = ({
  rows = 2,
  twoColumn = false,
}) => (
  <Card>
    <CardHeader>
      <Skeleton className="h-5 w-36" />
    </CardHeader>
    <CardContent className={twoColumn ? "grid gap-4 sm:grid-cols-2" : "flex flex-col gap-4"}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-9 w-full" />
        </div>
      ))}
    </CardContent>
  </Card>
);

const TableOfContentsSkeleton: FC = () => (
  <aside className="sticky top-6 hidden self-start lg:block">
    <nav className="rounded-xl border bg-card p-4">
      <Skeleton className="mb-3 h-3 w-20" />
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2 px-2 py-1.5">
            <Skeleton className="h-3 w-5" />
            <Skeleton className="h-4 w-28" />
          </div>
        ))}
      </div>
    </nav>
  </aside>
);

interface EditPageLoadingProps {
  parentLink: string;
  parentLabel: ReactNode;
  current: ReactNode;
  layout?: "form-grid" | "narrow";
}

export const EditPageLoading: FC<EditPageLoadingProps> = ({
  parentLink,
  parentLabel,
  current,
  layout = "form-grid",
}) => {
  const breadcrumb = useNavHeader(
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink render={<Link to={parentLink} />}>{parentLabel}</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{current}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>,
  );

  return (
    <>
      {breadcrumb}
      <div
        className="-m-4 flex min-h-0 flex-1 flex-col overflow-hidden"
        aria-busy="true"
        aria-label={m.edit_page_loading()}
      >
        <ScrollArea className="min-h-0 flex-1">
          <Separator />
          {layout === "form-grid" ? (
            <FormPageLayoutGrid aside={<TableOfContentsSkeleton />}>
              <SkeletonCard rows={1} />
              <SkeletonCard rows={3} twoColumn />
              <SkeletonCard rows={2} twoColumn />
              <SkeletonCard rows={1} />
            </FormPageLayoutGrid>
          ) : (
            <div className="mx-auto max-w-2xl px-6 py-6">
              <div className="flex flex-col gap-4">
                <SkeletonCard rows={2} />
                <SkeletonCard rows={2} />
                <SkeletonCard rows={2} twoColumn />
              </div>
            </div>
          )}
        </ScrollArea>
        <footer className="shrink-0 flex items-center gap-4 border-t bg-background px-6 py-3">
          <div className="flex-1" />
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-24" />
        </footer>
      </div>
    </>
  );
};
